import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  Mail,
  MailOpen,
  AlertTriangle,
  Loader2,
} from 'lucide-react'
import { useSendReminder } from '../../hooks/useSignatures'
import type { SignatureRequestSummary } from '../../services/documentsApi'
import type { EmailStatus } from '../../services/signaturesApi'

interface SignerDetailProps {
  signer: SignatureRequestSummary
  emailStatus?: EmailStatus
  actorId: string
}

const signerStatusIcons: Record<string, typeof CheckCircle> = {
  signed: CheckCircle,
  rejected: XCircle,
  pending: Clock,
  waiting: Clock,
  expired: AlertTriangle,
  cancelled: XCircle,
}

const signerStatusColors: Record<string, string> = {
  signed: 'text-success-500',
  rejected: 'text-error-500',
  pending: 'text-warning-500',
  waiting: 'text-neutral-400',
  expired: 'text-neutral-400',
  cancelled: 'text-neutral-400',
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Can send a reminder only if status is pending and no reminder in last 24h */
function canSendReminder(signer: SignatureRequestSummary): boolean {
  if (signer.status !== 'pending') return false
  if (!signer.last_reminder_sent) return true
  const lastSent = new Date(signer.last_reminder_sent).getTime()
  const hoursAgo = (Date.now() - lastSent) / (1000 * 60 * 60)
  return hoursAgo >= 24
}

export function SignerDetail({ signer, emailStatus, actorId }: SignerDetailProps) {
  const { t } = useTranslation()
  const reminderMutation = useSendReminder()
  const [reminderError, setReminderError] = useState<string | null>(null)

  const StatusIcon = signerStatusIcons[signer.status] || Clock
  const statusColor = signerStatusColors[signer.status] || 'text-neutral-400'
  const showReminder = canSendReminder(signer)

  const handleSendReminder = () => {
    setReminderError(null)
    reminderMutation.mutate(
      { signatureRequestId: signer.koblink_id, actorId },
      {
        onError: (error) => {
          setReminderError(error instanceof Error ? error.message : t('common.error'))
        },
      },
    )
  }

  return (
    <div className="py-2 px-1">
      <div className="flex items-center gap-2">
        <StatusIcon size={14} className={`shrink-0 ${statusColor}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-neutral-800 dark:text-neutral-200 truncate">
            {signer.invitee_name}
          </p>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
            {signer.invited_email}
          </p>
        </div>
        <span className="text-[10px] text-neutral-400 shrink-0">
          {t(`followup.signerStatus.${signer.status}`)}
        </span>
      </div>

      {/* Signed/rejected timestamp */}
      {signer.signed_at && (
        <p className="text-[10px] text-success-500 mt-1 ml-5">
          {t('followup.signedAt', { time: formatDate(signer.signed_at) })}
        </p>
      )}
      {signer.rejected_at && (
        <p className="text-[10px] text-error-500 mt-1 ml-5">
          {t('followup.rejectedAt', { time: formatDate(signer.rejected_at) })}
          {signer.rejection_reason && ` — ${signer.rejection_reason}`}
        </p>
      )}

      {/* Email delivery status */}
      {emailStatus && (
        <div className="flex items-center gap-1.5 mt-1 ml-5">
          <EmailStatusIcon status={emailStatus.current_status} hasProblem={emailStatus.has_problem} />
          <span className={`text-[10px] ${emailStatus.has_problem ? 'text-error-500' : 'text-neutral-400'}`}>
            {t(`followup.emailStatus.${emailStatus.current_status}`, { defaultValue: emailStatus.current_status })}
          </span>
        </div>
      )}

      {/* Reminder info + button */}
      {signer.reminder_count > 0 && (
        <p className="text-[10px] text-neutral-400 mt-1 ml-5">
          {t('followup.remindersSent', { count: signer.reminder_count })}
          {signer.last_reminder_sent && ` · ${formatDate(signer.last_reminder_sent)}`}
        </p>
      )}

      {showReminder && (
        <div className="mt-1.5 ml-5">
          <button
            onClick={handleSendReminder}
            disabled={reminderMutation.isPending}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-warning-50 dark:bg-warning-900/30 text-warning-600 dark:text-warning-400 hover:bg-warning-100 dark:hover:bg-warning-900/50 disabled:opacity-50 transition-colors"
          >
            {reminderMutation.isPending ? (
              <Loader2 size={10} className="animate-spin" />
            ) : (
              <Bell size={10} />
            )}
            {t('followup.sendReminder')}
          </button>
          {reminderMutation.isSuccess && (
            <span className="text-[10px] text-success-500 ml-2">{t('followup.reminderSent')}</span>
          )}
          {reminderError && (
            <p className="text-[10px] text-error-500 mt-0.5">{reminderError}</p>
          )}
        </div>
      )}
    </div>
  )
}

function EmailStatusIcon({ status, hasProblem }: { status: string; hasProblem: boolean }) {
  if (hasProblem) return <AlertTriangle size={10} className="text-error-500" />
  if (status === 'opened' || status === 'clicked') return <MailOpen size={10} className="text-success-500" />
  return <Mail size={10} className="text-neutral-400" />
}
